<template>
    <div class="scheduled-changes-container">
        <ds-inline-alert
            class="scheduled-changes-alert"
            leading-icon
            type="info"
        >
            {{ $t('myaccount.quick.add.has.scheduled.changes.inline.alert') }}
        </ds-inline-alert>

        <p class="scheduled-changes-title">
            {{ $t('myaccount.quick.add.has.scheduled.changes.detail.header') }}
        </p>

        <p class="scheduled-changes-sub-title">
            {{ $t('myaccount.quick.add.has.scheduled.changes.diff.sentence.start') }}
        </p>

        <ul class="scheduled-changes-description">
            <li v-if="editionChangeSentence">
                {{ editionChangeSentence }}
            </li>
            <li v-if="additionSentence">
                {{ additionSentence }}
            </li>
            <li v-if="removalSentence">
                {{ removalSentence }}
            </li>
        </ul>

        <p class="billing-text">
            {{ billingText }}
        </p>

        <ds-filled-button
            class="scheduled-changes-description-action-button"
            data-qa="scheduled-changes-description-action-button"
            @click="acknowledgeModal"
        >
            {{ $t('myaccount.quick.add.has.scheduled.changes.continue.button') }}
        </ds-filled-button>
    </div>
</template>

<script>
import amplitude from '@/components/util/analytics/amplitude';
import moment from 'moment';

const CONTACT = 'CONTACT';
const USER = 'USER';
const SMS = 'SMS';
const OPERATION_REMOVE = 'REMOVE';
const OPERATION_ADD = 'ADD';
const PLAN = 'PLAN';

export default {
    props: {
        differences: {
            type: Array,
        },
        nextBillingDate: {
            type: String,
        },
        billingFrequency: {
            type: String,
        },
        currencySymbol: {
            type: String,
        },
        scheduledChangesEstimateTotal: {
            type: Number,
        },
        currentPlan: {
            type: Object,
        },
    },

    emits: ['close-notice'],

    created() {
        this.logOpened();
    },

    computed: {
        additionSentence() {
            if (this.differences == null) {
                return null;
            }

            const sentenceParts = [];

            const { combinedAdditionChanges } = this.combinedChanges;

            if (combinedAdditionChanges) {
                sentenceParts.push(this.$t('myaccount.quick.add.has.scheduled.changes.diff.sentence.adding.phrase', { value: combinedAdditionChanges }));
            }

            if (sentenceParts.length === 0) {
                return '';
            }

            return `${sentenceParts.join(' ')}`;
        },

        removalSentence() {
            if (this.differences == null) {
                return null;
            }

            const sentenceParts = [];

            const { combinedRemovalChanges } = this.combinedChanges;

            if (combinedRemovalChanges) {
                sentenceParts.push(this.$t('myaccount.quick.add.has.scheduled.changes.diff.sentence.removing.phrase', { value: combinedRemovalChanges }));
            } else {
                return '';
            }

            if (sentenceParts.length === 0) {
                return '';
            }

            return `${sentenceParts.join(' ')}`;
        },

        editionChangeSentence() {
            const plan = this.differences.find((item) => item.type === PLAN);

            if (!plan) {
                return null;
            }

            return this.$t('myaccount.quick.add.has.scheduled.changes.diff.plan.change.sentence', {
                futureEdition: plan.name,
            });
        },

        billingText() {
            return this.$t('myaccount.quick.add.has.scheduled.changes.diff.sentence.billing', {
                date: this.dateFormatDate(this.nextBillingDate),
                term: this.formattedBillingFrequency,
                currencySymbol: this.currencySymbol,
                amount: this.scheduledChangesEstimateTotal,
            });
        },

        combinedChanges() {
            if (this.differences == null) {
                return {};
            }

            const combineDifferences = (differences) => {
                let combinedChanges = '';

                if (differences.length === 0) {
                    combinedChanges = '';
                } else if (differences.length === 1) {
                    combinedChanges = differences.at(0);
                } else if (differences.length === 2) {
                    combinedChanges = this.$t('myaccount.quick.add.has.scheduled.changes.diff.sentence.twoValues', {
                        valueOne: differences.at(0),
                        valueTwo: differences.at(1),
                    });
                } else {
                    const last = differences.at(-1);
                    const nextToLast = differences.at(-2);
                    const rest = differences.slice(0, differences.length - 2);

                    combinedChanges = this.$t('myaccount.quick.add.has.scheduled.changes.diff.sentence.threeOrMoreValues', {
                        firstValues: rest.join(', '),
                        nextToLastValue: nextToLast,
                        lastValue: last,
                    });
                }

                return combinedChanges;
            };

            const { removals, additions } = this.differencesByType;

            const combinedRemovalChanges = combineDifferences(removals);
            const combinedAdditionChanges = combineDifferences(additions);

            return { combinedRemovalChanges, combinedAdditionChanges };
        },

        differencesByType() {
            if (this.differences == null) {
                return null;
            }

            const addSentenceToArray = (item, array) => {
                const change = Math.abs(item.change);

                // Do not add sentence to array if the total change is 0.
                if (change === 0) {
                    return;
                }

                switch (item.type) {
                case CONTACT:
                    array.push(this.$t('myaccount.quick.add.has.scheduled.changes.diff.sentence.contacts', { number: change }));
                    break;
                case SMS:
                    array.push(this.$t('myaccount.quick.add.has.scheduled.changes.diff.sentence.textMessages', { number: change }));
                    break;
                case USER:
                    array.push(
                        this.$tc('myaccount.quick.add.has.scheduled.changes.diff.sentence.users', change,
                            { number: change }),
                    );
                    break;
                // Classic does not currently support MINUTES and LOCAL_PHONE_NUMBER
                default:
                    break;
                }
            };

            const removals = [];
            const additions = [];

            this.differences.forEach((item) => {
                if (item.operation === OPERATION_REMOVE) {
                    addSentenceToArray(item, removals);
                } else if (item.operation === OPERATION_ADD) {
                    addSentenceToArray(item, additions);
                }
            });

            return { additions, removals };
        },

        formattedBillingFrequency() {
            if (this.billingFrequency == null) {
                return null;
            }

            if (this.billingFrequency === 'MONTH') {
                return 'monthly';
            }

            return 'yearly';
        },
    },

    methods: {
        dateFormatDate(date, format = 'll') {
            const formattedDate = moment.utc(date).isValid() ? date : new Date(date);

            return moment(formattedDate).format(format);
        },

        logOpened() {
            amplitude.infusionsoft.track(amplitude.keap.events.SUBSCRIPTION_MANAGEMENT_QUICK_ADD, {
                Event: 'Scheduled Changes notice seen',
            });
        },
        acknowledgeModal() {
            this.$emit('close-notice');
        },
    },
};
</script>

<style lang="scss" scoped>
ul {
    padding: 0 0 $spacing-200 $spacing-250;
    font-size: $font-size-sm;
}

li {
    list-style: disc;
}

.scheduled-changes-sub-title {
    margin-bottom: 0;
}

.scheduled-changes-container {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
}

.scheduled-changes-alert {
    margin-bottom: 2rem;
}

.scheduled-changes-title {
    font-size: $font-size-md;
    font-weight: 500;
}

.scheduled-changes-description {
    font-size: $font-size-sm;
}
</style>
